import { useMemo, useState } from 'react';
import { Segmented } from '../../components/data';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DeleteButton, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';

/**
 * Integration Plan — a board of workstreams and steps.
 *
 * Ported from the standalone M&A app, which used @dnd-kit for the drag. Kept
 * rather than hand-rolled: it brings keyboard dragging with it, and a bespoke
 * mousedown implementation would be a worse one that only works with a mouse.
 */
const STEP_STATUSES = [
  { value: 'not_started', label: 'Not started', badge: 'badge-neutral' },
  { value: 'in_progress', label: 'In progress', badge: 'badge-warning' },
  { value: 'blocked', label: 'Blocked', badge: 'badge-critical' },
  { value: 'done', label: 'Done', badge: 'badge-good' },
] as const;

const statusMeta = (v: string) => STEP_STATUSES.find((s) => s.value === v) ?? STEP_STATUSES[0];

interface PlanRow {
  MaPlanId: string;
  Name: string;
  Description: string;
  /** A template (migration 159): copied to start a plan, never worked itself. */
  IsTemplate?: boolean;
  /** Archived (migration 163): off the picker, admins only. */
  ArchivedUtc?: string | null;
  Workstreams: number;
  Steps: number;
  StepsDone: number;
}
interface Phase {
  MaPhaseId: string;
  Name: string;
  SortOrder: number;
  /** Kept to admins (migration 163): never sent to anyone else. */
  AdminOnly?: boolean;
}
interface Step {
  MaStepId: string;
  MaPhaseId: string;
  Title: string;
  Description: string;
  Owner: string;
  Status: string;
  StartDate: string;
  DueDate: string;
  Priority: string;
  EconomicImpact: string;
  SortOrder: number;
  /** Assigned to an employee (migration 164), beside the free-text Owner. */
  AssigneeEmployeeId?: number | null;
  AssigneeName?: string | null;
}
interface Person {
  EmployeeId: number;
  Name: string;
}
interface People {
  people: Person[];
  meEmployeeId: number | null;
}
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
interface Substep {
  MaSubstepId: string;
  MaStepId: string;
  Title: string;
  Status: string;
  SortOrder: number;
}
interface Board {
  plan: PlanRow;
  phases: Phase[];
  steps: Step[];
  substeps: Substep[];
}

/**
 * `isAdmin` decides the controls, not what is shown: the API already leaves
 * out every Admins only list for anyone else, and refuses their changes to the
 * plan's shape. Admins also get templates, new plans and the list controls.
 */
export function MaIntegrationPlan({ isAdmin }: { isAdmin: boolean }) {
  const plans = useResource<PlanRow[]>('/ma/plans');
  const [planId, setPlanId] = useState<string | null>(null);

  /* What is open, the dialogs, and a save in flight. */
  const [dialog, setDialog] = useState<null | 'new' | 'template'>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const list = plans.data ?? [];
  const live = list.filter((p) => !p.IsTemplate && !p.ArchivedUtc);
  const templates = list.filter((p) => p.IsTemplate);
  const archived = list.filter((p) => !p.IsTemplate && p.ArchivedUtc);
  const chosen =
    (planId && list.some((p) => p.MaPlanId === planId) ? planId : null) ?? live[0]?.MaPlanId ?? templates[0]?.MaPlanId ?? null;
  const current = list.find((p) => p.MaPlanId === chosen);

  if (plans.loading) return <Loading label="Loading plans" />;
  if (plans.error) return <ErrorBanner message={plans.error} />;

  const removeTemplate = async (p: PlanRow) => {
    setProblem(null);
    try {
      await api.del(`/ma/plans/${p.MaPlanId}`);
      setPlanId(null);
      plans.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not delete the template');
    }
  };

  /* Archive or restore a plan; delete one for good. Admins only (the API agrees). */
  const setArchived = async (p: PlanRow, archive: boolean) => {
    setProblem(null);
    try {
      await api.put(`/ma/plans/${p.MaPlanId}/archive`, { archived: archive });
      if (archive) setPlanId(null);
      else setPlanId(p.MaPlanId);
      plans.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : archive ? 'Could not archive the plan' : 'Could not restore the plan');
    }
  };
  const removePlan = async (p: PlanRow) => {
    setProblem(null);
    try {
      await api.del(`/ma/plans/${p.MaPlanId}`);
      setPlanId(null);
      plans.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not delete the plan');
    }
  };

  return (
    <div className="stack">
      {problem && <ErrorBanner message={problem} />}
      <div className="card">
        <div className="card-body tight">
          <div className="row-between wrap" style={{ gap: 'var(--space-2)' }}>
            <div className="row wrap" style={{ gap: 'var(--space-2)' }}>
              <span className="eyebrow">Plan</span>
              {live.length === 0 && <span className="xs muted">No plans yet.</span>}
              {live.map((p) => (
                <button
                  key={p.MaPlanId}
                  type="button"
                  className={`btn btn-sm ${chosen === p.MaPlanId ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setPlanId(p.MaPlanId)}
                >
                  {p.Name}
                  <span className="xs" style={{ marginLeft: 6, opacity: 0.75 }}>
                    {p.StepsDone}/{p.Steps}
                  </span>
                </button>
              ))}
            </div>
            {isAdmin && (
              <div className="row wrap" style={{ gap: 'var(--space-2)' }}>
                {current && !current.IsTemplate && !current.ArchivedUtc && (
                  <>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDialog('template')}>
                      Save as template
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      title="Take it off the plan list and hide it from everyone but admins — nothing in it is lost"
                      onClick={() => void setArchived(current, true)}
                    >
                      Archive
                    </button>
                    <DeleteButton
                      what={`the plan "${current.Name}" — every list, step and checklist in it`}
                      label="Delete"
                      onConfirm={() => void removePlan(current)}
                    />
                  </>
                )}
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setDialog('new')}>
                  New plan
                </button>
              </div>
            )}
          </div>
          {templates.length > 0 && (
            <div className="row wrap mt-2" style={{ gap: 'var(--space-2)' }}>
              <span className="eyebrow">Templates</span>
              {templates.map((p) => (
                <button
                  key={p.MaPlanId}
                  type="button"
                  className={`btn btn-sm ${chosen === p.MaPlanId ? 'btn-secondary' : 'btn-ghost'}`}
                  onClick={() => setPlanId(p.MaPlanId)}
                  title="Open the template to edit it — start a plan from it with New plan"
                >
                  {p.Name}
                  <span className="xs" style={{ marginLeft: 6, opacity: 0.75 }}>
                    {p.Steps} steps
                  </span>
                </button>
              ))}
            </div>
          )}
          {isAdmin && archived.length > 0 && (
            <div className="row wrap mt-2" style={{ gap: 'var(--space-2)' }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowArchived((v) => !v)}>
                {showArchived ? '▾' : '▸'} Archived ({archived.length})
              </button>
              {showArchived &&
                archived.map((p) => (
                  <button
                    key={p.MaPlanId}
                    type="button"
                    className={`btn btn-sm ${chosen === p.MaPlanId ? 'btn-secondary' : 'btn-ghost'}`}
                    onClick={() => setPlanId(p.MaPlanId)}
                  >
                    {p.Name}
                    <span className="xs" style={{ marginLeft: 6, opacity: 0.75 }}>
                      {p.StepsDone}/{p.Steps}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>

      {current?.ArchivedUtc && !current.IsTemplate && (
        <div className="card">
          <div className="card-body tight row-between wrap">
            <span className="small">
              <span className="badge badge-neutral">Archived</span> <strong>{current.Name}</strong> is archived: off the plan list and
              hidden from everyone but admins.
            </span>
            <span className="row" style={{ gap: 'var(--space-2)' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => void setArchived(current, false)}>
                Restore
              </button>
              <DeleteButton
                what={`the plan "${current.Name}" — every list, step and checklist in it`}
                label="Delete"
                onConfirm={() => void removePlan(current)}
              />
            </span>
          </div>
        </div>
      )}

      {current?.IsTemplate && (
        <div className="card">
          <div className="card-body tight row-between wrap">
            <span className="small">
              <span className="badge badge-neutral">Template</span> Editing the template <strong>{current.Name}</strong>. Changes here
              shape every plan started from it from now on; plans already started are not changed.
            </span>
            <DeleteButton what={`the template "${current.Name}"`} label="Delete template" onConfirm={() => void removeTemplate(current)} />
          </div>
        </div>
      )}

      {chosen ? (
        <PlanBoard planId={chosen} isAdmin={isAdmin} onChanged={() => plans.reload()} />
      ) : (
        <div className="card">
          <div className="card-body">
            <p className="muted">
              {isAdmin ? 'No integration plans yet. Start one with New plan.' : 'There is no integration plan to show yet.'}
            </p>
          </div>
        </div>
      )}

      {dialog === 'new' && (
        <NewPlanDialog
          templates={templates}
          onClose={() => setDialog(null)}
          onCreated={(id) => {
            setDialog(null);
            setPlanId(id);
            plans.reload();
          }}
        />
      )}
      {dialog === 'template' && current && (
        <SaveTemplateDialog
          plan={current}
          onClose={() => setDialog(null)}
          onSaved={(id) => {
            setDialog(null);
            setPlanId(id);
            plans.reload();
          }}
        />
      )}
    </div>
  );
}

/** Start a plan: blank, or as a copy of a template (statuses and dates fresh). */
function NewPlanDialog({
  templates,
  onClose,
  onCreated,
}: {
  templates: PlanRow[];
  onClose: () => void;
  onCreated: (planId: string) => void;
}) {
  const [name, setName] = useState('');
  const [from, setFrom] = useState(templates[0]?.MaPlanId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = from
        ? await api.post<{ MaPlanId: string }>(`/ma/plans/${from}/copy`, { name: name.trim(), asTemplate: false })
        : await api.post<{ MaPlanId: string }>('/ma/plans', { name: name.trim() });
      onCreated(r.MaPlanId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the plan');
      setBusy(false);
    }
  };
  return (
    <Modal
      title="New integration plan"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void create()}>
            {busy ? 'Creating…' : 'Create plan'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="Plan name">
        <input className="input" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="e.g. NewCo integration" maxLength={300} />
      </Field>
      <Field label="Start from" hint={from ? 'Every list, step and checklist is copied; statuses start at not started and dates are blank.' : undefined}>
        <select className="select" value={from} onChange={(e) => setFrom(e.target.value)}>
          <option value="">A blank plan</option>
          {templates.map((t) => (
            <option key={t.MaPlanId} value={t.MaPlanId}>
              Template: {t.Name} ({t.Workstreams} lists, {t.Steps} steps)
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  );
}

/** Save the open plan as a template: its shape, without its progress. */
function SaveTemplateDialog({
  plan,
  onClose,
  onSaved,
}: {
  plan: PlanRow;
  onClose: () => void;
  onSaved: (planId: string) => void;
}) {
  const [name, setName] = useState(`${plan.Name} template`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ MaPlanId: string }>(`/ma/plans/${plan.MaPlanId}/copy`, { name: name.trim(), asTemplate: true });
      onSaved(r.MaPlanId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the template');
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Save as template"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save template'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <p className="small muted">
        Copies <strong>{plan.Name}</strong> — its {plan.Workstreams} lists, {plan.Steps} steps and their checklists, with owners,
        priorities and descriptions — as a template. Statuses go back to not started and dates are cleared. The plan itself is not
        changed.
      </p>
      <Field label="Template name">
        <input className="input" value={name} autoFocus onChange={(e) => setName(e.target.value)} maxLength={300} />
      </Field>
    </Modal>
  );
}

/*
 * Column widths: one width for every list, chosen above the board, and any
 * list dragged wider or narrower by its right-hand edge. A viewing preference,
 * so kept in this browser (localStorage), not in the plan.
 */
const WIDTH_KEY = 'coo-ma-col-widths';
const MIN_W = 200;
const MAX_W = 720;
const WIDTHS = [
  { id: '220', label: 'Narrow' },
  { id: '260', label: 'Normal' },
  { id: '340', label: 'Wide' },
  { id: '440', label: 'Extra wide' },
];
interface Widths {
  all: number;
  cols: Record<string, number>;
}
const clampW = (w: number) => Math.round(Math.min(MAX_W, Math.max(MIN_W, w)));
function readWidths(): Widths {
  try {
    const v = JSON.parse(localStorage.getItem(WIDTH_KEY) ?? 'null') as Partial<Widths> | null;
    if (v && typeof v.all === 'number') return { all: clampW(v.all), cols: v.cols ?? {} };
  } catch {
    /* none saved, or storage blocked */
  }
  return { all: 260, cols: {} };
}
function saveWidths(w: Widths) {
  try {
    localStorage.setItem(WIDTH_KEY, JSON.stringify(w));
  } catch {
    /* the widths still apply until the page is left */
  }
}

function PlanBoard({ planId, isAdmin, onChanged }: { planId: string; isAdmin: boolean; onChanged: () => void }) {
  const [widths, setWidths] = useState<Widths>(readWidths);
  const updateWidths = (next: Widths, persist: boolean) => {
    setWidths(next);
    if (persist) saveWidths(next);
  };
  const board = useResource<Board>(`/ma/plans/${planId}`);
  const people = useResource<People>('/ma/plans/people');
  /* Whose steps to pick out: everyone, me, or one person. Others are dimmed, not
     hidden, so a drag still lands where it looks like it will. */
  const [assignee, setAssignee] = useState<string>('');
  const [openStep, setOpenStep] = useState<string | null>(null);
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /* The card being dragged: drawn in an overlay above every column, because
     each column scrolls on its own and would clip the card the moment it
     left — which is what made dropping into another column impossible. */
  const [dragging, setDragging] = useState<string | null>(null);

  const sensors = useSensors(
    /* A small distance before a drag starts, so clicking a card to open it
       does not register as a one-pixel drag. */
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const phases = useMemo(() => board.data?.phases ?? [], [board.data]);
  const steps = useMemo(() => board.data?.steps ?? [], [board.data]);
  const substeps = useMemo(() => board.data?.substeps ?? [], [board.data]);

  const byPhase = useMemo(() => {
    const map = new Map<string, Step[]>();
    for (const p of phases) map.set(p.MaPhaseId, []);
    for (const s of steps) map.get(s.MaPhaseId)?.push(s);
    for (const list of map.values()) list.sort((a, b) => a.SortOrder - b.SortOrder);
    return map;
  }, [phases, steps]);

  /* What is under the pointer wins — a card first (drop beside it), else the
     column (drop at its end); nearest corner only when the pointer is over
     neither, as between columns. */
  const collision: CollisionDetection = (args) => {
    const hits = pointerWithin(args);
    if (hits.length) {
      const card = hits.find((h) => steps.some((s) => s.MaStepId === String(h.id)));
      return card ? [card] : hits;
    }
    return closestCorners(args);
  };

  const onDragEnd = async (e: DragEndEvent) => {
    setDragging(null);
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || activeId === overId) return;

    const moved = steps.find((s) => s.MaStepId === activeId);
    if (!moved) return;

    /* The drop target is either a card (drop beside it) or a column (drop at
       the end of it). Both resolve to "which column, and in what position". */
    const overStep = steps.find((s) => s.MaStepId === overId);
    const targetPhase = overStep ? overStep.MaPhaseId : overId;
    if (!phases.some((p) => p.MaPhaseId === targetPhase)) return;

    const target = (byPhase.get(targetPhase) ?? []).filter((s) => s.MaStepId !== activeId);
    const index = overStep ? target.findIndex((s) => s.MaStepId === overId) : target.length;
    target.splice(index < 0 ? target.length : index, 0, moved);

    setProblem(null);
    /* Shown in its new place at once; the save follows. */
    const placed = new Map(target.map((s, i) => [s.MaStepId, (i + 1) * 10]));
    board.set((cur) => ({
      ...cur,
      steps: cur.steps.map((s) => (placed.has(s.MaStepId) ? { ...s, MaPhaseId: targetPhase, SortOrder: placed.get(s.MaStepId)! } : s)),
    }));
    try {
      await api.post(`/ma/plans/${planId}/reorder`, {
        steps: target.map((s, i) => ({
          id: s.MaStepId,
          phaseId: targetPhase,
          sortOrder: (i + 1) * 10,
        })),
      });
      board.reload();
      onChanged();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not move it');
      board.reload();
    }
  };

  if (board.loading) return <Loading label="Loading the board" />;
  if (board.error) return <ErrorBanner message={board.error} />;

  /* Move a whole list one place left or right: shown at once, then saved as
     the full left-to-right order. */
  const moveList = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= phases.length) return;
    const next = [...phases];
    [next[index], next[to]] = [next[to]!, next[index]!];
    board.set((cur) => ({ ...cur, phases: next.map((p, i) => ({ ...p, SortOrder: (i + 1) * 10 })) }));
    void api
      .put(`/ma/plans/${planId}/phases/order`, { ids: next.map((p) => p.MaPhaseId) })
      .catch((err: unknown) => {
        setProblem(err instanceof Error ? err.message : 'Could not move the list');
        board.reload();
      });
  };

  const step = steps.find((s) => s.MaStepId === openStep) ?? null;

  const matchesAssignee = (s: Step) => {
    if (!assignee) return true;
    if (assignee === 'none') return s.AssigneeEmployeeId == null;
    const id = assignee === 'me' ? people.data?.meEmployeeId : Number(assignee);
    return s.AssigneeEmployeeId === id;
  };

  return (
    <>
      {problem && <ErrorBanner message={problem} />}

      <div className="row ma-width-bar" style={{ gap: 8 }}>
        <span className="xs muted">Column width</span>
        <Segmented
          label="Column width"
          options={WIDTHS}
          value={(WIDTHS.find((o) => Number(o.id) === widths.all && !Object.keys(widths.cols).length)?.id ?? '') as string}
          onChange={(id) => updateWidths({ all: Number(id), cols: {} }, true)}
        />
        <span className="xs muted">or drag a list's right-hand edge.</span>
        <span className="ma-width-sep" aria-hidden="true" />
        <label className="xs muted" htmlFor="ma-assignee-filter">
          Steps for
        </label>
        <select
          id="ma-assignee-filter"
          className="select select-sm"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
        >
          <option value="">Everyone</option>
          {people.data?.meEmployeeId != null && <option value="me">Me</option>}
          <option value="none">Nobody yet</option>
          {(people.data?.people ?? []).map((p) => (
            <option key={p.EmployeeId} value={String(p.EmployeeId)}>
              {p.Name}
            </option>
          ))}
        </select>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={collision}
        onDragStart={(e) => setDragging(String(e.active.id))}
        onDragCancel={() => setDragging(null)}
        onDragEnd={(e) => void onDragEnd(e)}
      >
        <div className="ma-board">
          {phases.map((phase, index) => {
            const cards = byPhase.get(phase.MaPhaseId) ?? [];
            const done = cards.filter((c) => c.Status === 'done').length;

            return (
              <Column
                key={phase.MaPhaseId}
                phase={phase}
                isAdmin={isAdmin}
                onAdminOnly={(adminOnly) => {
                  board.set((cur) => ({
                    ...cur,
                    phases: cur.phases.map((p) => (p.MaPhaseId === phase.MaPhaseId ? { ...p, AdminOnly: adminOnly } : p)),
                  }));
                  void api
                    .put(`/ma/plans/${planId}/phases/${phase.MaPhaseId}`, { adminOnly })
                    .then(() => onChanged())
                    .catch((err: unknown) => {
                      setProblem(err instanceof Error ? err.message : 'Could not change who sees the list');
                      board.reload();
                    });
                }}
                done={done}
                total={cards.length}
                onMove={(dir) => moveList(index, dir)}
                width={widths.cols[phase.MaPhaseId] ?? widths.all}
                onResize={(w, done) =>
                  updateWidths({ ...widths, cols: { ...widths.cols, [phase.MaPhaseId]: clampW(w) } }, done)
                }
                onResetWidth={() => {
                  const cols = { ...widths.cols };
                  delete cols[phase.MaPhaseId];
                  updateWidths({ ...widths, cols }, true);
                }}
                onDelete={() =>
                  void api
                    .del(`/ma/plans/${planId}/phases/${phase.MaPhaseId}`)
                    .then(() => {
                      board.reload();
                      onChanged();
                    })
                    .catch((err: unknown) => setProblem(err instanceof Error ? err.message : 'Could not delete the list'))
                }
                canMoveLeft={index > 0}
                canMoveRight={index < phases.length - 1}
                onRename={(name) =>
                  void api
                    .put(`/ma/plans/${planId}/phases/${phase.MaPhaseId}`, { name })
                    .then(() => board.reload())
                    .catch((err: unknown) => setProblem(err instanceof Error ? err.message : 'Could not rename the list'))
                }
              >
                <SortableContext
                  items={cards.map((c) => c.MaStepId)}
                  strategy={verticalListSortingStrategy}
                >
                  {cards.map((card) => (
                    <StepCard
                      key={card.MaStepId}
                      dim={!matchesAssignee(card)}
                      step={card}
                      substeps={substeps.filter((s) => s.MaStepId === card.MaStepId)}
                      onOpen={() => setOpenStep(card.MaStepId)}
                    />
                  ))}
                </SortableContext>

                <button
                  type="button"
                  className="btn btn-ghost btn-sm ma-col-add"
                  onClick={() => setAddingTo(phase.MaPhaseId)}
                >
                  + Add a step
                </button>
              </Column>
            );
          })}
          {isAdmin && (
            <AddList
              planId={planId}
              onAdded={() => {
                board.reload();
                onChanged();
              }}
            />
          )}
        </div>
        <DragOverlay dropAnimation={null}>
          {dragging
            ? (() => {
                const s = steps.find((x) => x.MaStepId === dragging);
                return s ? <CardFace step={s} substeps={substeps.filter((x) => x.MaStepId === s.MaStepId)} overlay /> : null;
              })()
            : null}
        </DragOverlay>
      </DndContext>

      {step && (
        <StepEditor
          planId={planId}
          step={step}
          people={people.data?.people ?? []}
          substeps={substeps.filter((s) => s.MaStepId === step.MaStepId)}
          onClose={() => setOpenStep(null)}
          onSaved={() => {
            board.reload();
            onChanged();
          }}
          onDeleted={() => {
            setOpenStep(null);
            board.reload();
            onChanged();
          }}
        />
      )}

      {addingTo && (
        <AddStep
          planId={planId}
          phaseId={addingTo}
          onClose={() => setAddingTo(null)}
          onSaved={(id) => {
            setAddingTo(null);
            board.reload();
            onChanged();
            /* Straight into the new step, to fill in its owner, dates and checklist. */
            setOpenStep(id);
          }}
        />
      )}
    </>
  );
}

function Column({
  phase,
  isAdmin,
  onAdminOnly,
  done,
  total,
  onRename,
  onMove,
  onDelete,
  width,
  onResize,
  onResetWidth,
  canMoveLeft,
  canMoveRight,
  children,
}: {
  /** Width in pixels; dragged by the right-hand edge (`done` once let go). */
  width: number;
  onResize: (width: number, done: boolean) => void;
  onResetWidth: () => void;
  phase: Phase;
  /** Admins rename, move, delete and lock lists; anyone else just works the steps. */
  isAdmin: boolean;
  onAdminOnly: (adminOnly: boolean) => void;
  done: number;
  total: number;
  onRename: (name: string) => void;
  /** Delete the list and every step in it. */
  onDelete: () => void;
  /** Move the whole list one place left (-1) or right (+1). */
  onMove: (dir: -1 | 1) => void;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  children: React.ReactNode;
}) {
  /* The column is a drop target in its own right, so a card can be dropped
     into an empty one or below the last card. */
  const { setNodeRef, isOver } = useDroppable({ id: phase.MaPhaseId });

  return (
    <div
      ref={setNodeRef}
      className={`ma-col${isOver ? ' over' : ''}${phase.AdminOnly ? ' ma-col-admin' : ''}`}
      style={{ flex: `0 0 ${width}px` }}
    >
      {/* Drag to resize; double-click to go back to the board's width. */}
      <div
        className="ma-col-resize"
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${phase.Name}`}
        title="Drag to make this list wider or narrower · double-click to reset"
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const startX = e.clientX;
          const startW = width;
          const el = e.currentTarget;
          el.setPointerCapture(e.pointerId);
          let last = startW;
          const move = (ev: PointerEvent) => {
            last = startW + ev.clientX - startX;
            onResize(last, false);
          };
          const up = () => {
            el.removeEventListener('pointermove', move);
            el.removeEventListener('pointerup', up);
            el.removeEventListener('pointercancel', up);
            onResize(last, true);
          };
          el.addEventListener('pointermove', move);
          el.addEventListener('pointerup', up);
          el.addEventListener('pointercancel', up);
        }}
        onDoubleClick={onResetWidth}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') onResize(width + 20, true);
          if (e.key === 'ArrowLeft') onResize(width - 20, true);
        }}
        tabIndex={0}
      />
      <div className="ma-col-head">
        {isAdmin ? <ColumnName name={phase.Name} onRename={onRename} /> : <span className="ma-col-name">{phase.Name}</span>}
        <span className="row" style={{ gap: 4, flex: 'none' }}>
          <span className="xs muted">
            <span className="mono">{done}</span>/<span className="mono">{total}</span>
          </span>
          {isAdmin && (
            <button
              type="button"
              className={`ma-col-move ma-col-lock${phase.AdminOnly ? ' on' : ''}`}
              aria-pressed={Boolean(phase.AdminOnly)}
              onClick={() => onAdminOnly(!phase.AdminOnly)}
              onPointerDown={(e) => e.stopPropagation()}
              title={
                phase.AdminOnly
                  ? 'Admins only — hidden from everyone else. Click to show it to everyone with the page.'
                  : 'Seen by everyone with the page. Click to keep it to admins only.'
              }
              aria-label={phase.AdminOnly ? `${phase.Name}: admins only` : `Keep ${phase.Name} to admins only`}
            >
              {phase.AdminOnly ? '🔒' : '🔓'}
            </button>
          )}
          {isAdmin && (
            <>
              <button
                type="button"
                className="ma-col-move"
                disabled={!canMoveLeft}
                onClick={() => onMove(-1)}
                onPointerDown={(e) => e.stopPropagation()}
                title="Move this list left"
                aria-label={`Move ${phase.Name} left`}
              >
                ‹
              </button>
              <button
                type="button"
                className="ma-col-move"
                disabled={!canMoveRight}
                onClick={() => onMove(1)}
                onPointerDown={(e) => e.stopPropagation()}
                title="Move this list right"
                aria-label={`Move ${phase.Name} right`}
              >
                ›
              </button>
              <span className="ma-col-del" onPointerDown={(e) => e.stopPropagation()}>
                <DeleteButton
                  label="✕"
                  what={`the list "${phase.Name}"${total ? ` and its ${total} step${total === 1 ? '' : 's'}` : ''}`}
                  onConfirm={onDelete}
                />
              </span>
            </>
          )}
        </span>
      </div>
      {phase.AdminOnly && (
        <div className="ma-col-admin-note xs">Admins only · hidden from everyone else</div>
      )}
      <div className="ma-col-body">{children}</div>
    </div>
  );
}

/**
 * A list's header, renamed in place: click it (or the pencil), type, then
 * Enter or click away to save; Escape puts the old name back.
 */
function ColumnName({ name, onRename }: { name: string; onRename: (name: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const finish = (save: boolean) => {
    setEditing(false);
    const next = draft.trim();
    if (save && next && next !== name) onRename(next);
    else setDraft(name);
  };
  if (editing) {
    return (
      <input
        className="input input-sm ma-col-rename"
        value={draft}
        autoFocus
        maxLength={300}
        aria-label="List name"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') finish(true);
          if (e.key === 'Escape') finish(false);
        }}
        /* Keep a press in the box from starting a card drag on the column. */
        onPointerDown={(e) => e.stopPropagation()}
      />
    );
  }
  return (
    <button
      type="button"
      className="ma-col-name ma-col-name-btn"
      title="Rename this list"
      onClick={() => {
        setDraft(name);
        setEditing(true);
      }}
    >
      {name} <span className="ma-col-pencil" aria-hidden="true">✎</span>
    </button>
  );
}

/** A list added at the right-hand end of the board. */
function AddList({ planId, onAdded }: { planId: string; onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/ma/plans/${planId}/phases`, { name: name.trim() });
      setName('');
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the list');
    }
    setBusy(false);
  };
  return (
    <div className="ma-col ma-col-new">
      {open ? (
        <div className="ma-col-body">
          {error && <p className="xs" style={{ color: 'var(--status-danger)' }}>{error}</p>}
          <input
            className="input input-sm"
            autoFocus
            value={name}
            placeholder="Name the list"
            maxLength={300}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
              if (e.key === 'Escape') setOpen(false);
            }}
          />
          <div className="row mt-2" style={{ gap: 4 }}>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || !name.trim()} onClick={() => void add()}>
              {busy ? 'Adding…' : 'Add list'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-ghost ma-col-new-btn" onClick={() => setOpen(true)}>
          + Add a list
        </button>
      )}
    </div>
  );
}

function StepCard({
  step,
  substeps,
  onOpen,
  dim,
}: {
  /** Not the person the board is filtered to. */
  dim?: boolean;
  step: Step;
  substeps: Substep[];
  onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.MaStepId,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }}
      className={`ma-card${step.Status === 'done' ? ' ma-card-done' : ''}${dim ? ' ma-card-dim' : ''}`}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onOpen();
          return;
        }
        /* Keep keyboard dragging (space, arrows) working. */
        listeners?.onKeyDown?.(e);
      }}
    >
      <CardBody step={step} substeps={substeps} />
    </div>
  );
}

/** A card's contents — on the board, and floating under the pointer while dragged. */
function CardBody({ step, substeps }: { step: Step; substeps: Substep[] }) {
  const meta = statusMeta(step.Status);
  const doneSubs = substeps.filter((s) => s.Status === 'done').length;
  return (
    <>
      <div className="ma-card-title">{step.Title}</div>
      <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
        <span className={`badge ${meta.badge}`}>{meta.label}</span>
        {step.AssigneeName && (
          <span className="ma-assignee" title={`Assigned to ${step.AssigneeName}`}>
            <span className="ma-assignee-dot" aria-hidden="true">
              {initials(step.AssigneeName)}
            </span>
            <span className="xs">{step.AssigneeName}</span>
          </span>
        )}
        {step.Owner && <span className="xs muted">{step.Owner}</span>}
        {substeps.length > 0 && (
          <span className="xs subtle mono">
            {doneSubs}/{substeps.length}
          </span>
        )}
      </div>
      {step.DueDate && <div className="xs subtle mt-1">Due {step.DueDate}</div>}
    </>
  );
}

/** The card as drawn in the drag overlay. */
function CardFace({ step, substeps }: { step: Step; substeps: Substep[]; overlay?: boolean }) {
  return (
    <div className={`ma-card ma-card-overlay${step.Status === 'done' ? ' ma-card-done' : ''}`}>
      <CardBody step={step} substeps={substeps} />
    </div>
  );
}

function AddStep({
  planId,
  phaseId,
  onClose,
  onSaved,
}: {
  planId: string;
  phaseId: string;
  onClose: () => void;
  onSaved: (stepId: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await api.post<{ MaStepId: string }>(`/ma/plans/${planId}/steps`, { phaseId, title: title.trim() });
      onSaved(r.MaStepId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add it');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add a step"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="What is the step">
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && title.trim() && !saving) void submit();
          }}
          autoFocus
        />
      </Field>
      <p className="xs muted">It opens straight away for its owner, dates, priority and checklist.</p>
    </Modal>
  );
}

function StepEditor({
  planId,
  step,
  people,
  substeps,
  onClose,
  onSaved,
  onDeleted,
}: {
  planId: string;
  step: Step;
  /** Who it can be assigned to. */
  people: Person[];
  substeps: Substep[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [newSub, setNewSub] = useState('');

  const save = async (patch: Record<string, unknown>) => {
    setError(null);
    try {
      await api.put(`/ma/plans/${planId}/steps/${step.MaStepId}`, patch);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    }
  };

  const saveSub = async (id: string, patch: Record<string, unknown>) => {
    setError(null);
    try {
      await api.put(`/ma/plans/${planId}/substeps/${id}`, patch);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    }
  };

  const addSub = async () => {
    if (!newSub.trim()) return;
    setError(null);
    try {
      await api.post(`/ma/plans/${planId}/steps/${step.MaStepId}/substeps`, { title: newSub.trim() });
      setNewSub('');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add it');
    }
  };

  return (
    <Modal
      title={step.Title}
      onClose={onClose}
      footer={
        <>
          <DeleteButton
            what={step.Title}
            onConfirm={() => {
              void api.del(`/ma/plans/${planId}/steps/${step.MaStepId}`).then(onDeleted);
            }}
          />
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Title">
        <input
          className="input"
          defaultValue={step.Title}
          onBlur={(e) => {
            if (e.target.value.trim() && e.target.value !== step.Title)
              void save({ title: e.target.value });
          }}
        />
      </Field>

      <div className="form-grid">
        <Field label="Status">
          <select
            className="select"
            value={step.Status}
            onChange={(e) => void save({ status: e.target.value })}
          >
            {STEP_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Priority">
          <select className="select" value={step.Priority} onChange={(e) => void save({ priority: e.target.value })}>
            <option value="">—</option>
            {[...new Set(['High', 'Medium', 'Low', ...(step.Priority ? [step.Priority] : [])])].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Assigned to">
          <select
            className="select"
            value={step.AssigneeEmployeeId != null ? String(step.AssigneeEmployeeId) : ''}
            onChange={(e) => void save({ assigneeEmployeeId: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Nobody yet</option>
            {/* Someone no longer employed stays shown on the steps they had. */}
            {step.AssigneeEmployeeId != null && !people.some((p) => p.EmployeeId === step.AssigneeEmployeeId) && (
              <option value={String(step.AssigneeEmployeeId)}>{step.AssigneeName ?? 'A former employee'}</option>
            )}
            {people.map((p) => (
              <option key={p.EmployeeId} value={String(p.EmployeeId)}>
                {p.Name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Owner" hint="A team, or someone outside the company">
          <input
            className="input"
            defaultValue={step.Owner}
            onBlur={(e) => {
              if (e.target.value !== step.Owner) void save({ owner: e.target.value });
            }}
          />
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Start">
          <input
            className="input"
            defaultValue={step.StartDate}
            placeholder="—"
            onBlur={(e) => {
              if (e.target.value !== step.StartDate) void save({ startDate: e.target.value });
            }}
          />
        </Field>
        <Field label="Due">
          <input
            className="input"
            defaultValue={step.DueDate}
            placeholder="—"
            onBlur={(e) => {
              if (e.target.value !== step.DueDate) void save({ dueDate: e.target.value });
            }}
          />
        </Field>
      </div>

      <Field label="Description">
        <textarea
          className="input"
          rows={3}
          defaultValue={step.Description}
          onBlur={(e) => {
            if (e.target.value !== step.Description) void save({ description: e.target.value });
          }}
        />
      </Field>

      <Field label="Economic impact" hint="What this step is worth, or what it costs to skip.">
        <textarea
          className="input"
          rows={2}
          defaultValue={step.EconomicImpact}
          onBlur={(e) => {
            if (e.target.value !== step.EconomicImpact)
              void save({ economicImpact: e.target.value });
          }}
        />
      </Field>

      <div className="field">
        <label>Checklist</label>
        {substeps.length === 0 && <p className="xs subtle">Nothing on the checklist yet.</p>}
        <div className="stack mt-2" style={{ gap: 6 }}>
          {substeps.map((sub) => (
            <div key={sub.MaSubstepId} className={`row ma-sub-row${sub.Status === 'done' ? ' done' : ''}`} style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={sub.Status === 'done'}
                aria-label={`${sub.Title} done`}
                onChange={(e) =>
                  void saveSub(sub.MaSubstepId, { status: e.target.checked ? 'done' : 'not_started' })
                }
              />
              {/* Edit the item in place: saved when you leave the box. */}
              <input
                className={`input input-sm grow ma-sub-title${sub.Status === 'done' ? ' done' : ''}`}
                defaultValue={sub.Title}
                aria-label="Checklist item"
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== sub.Title) void saveSub(sub.MaSubstepId, { title: v });
                  else e.target.value = sub.Title;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
              />
              <button
                type="button"
                className="btn btn-ghost btn-icon btn-sm"
                title="Remove this item"
                aria-label={`Remove ${sub.Title}`}
                onClick={() =>
                  void api
                    .del(`/ma/plans/${planId}/substeps/${sub.MaSubstepId}`)
                    .then(onSaved)
                    .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not remove it'))
                }
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="row mt-2">
          <input
            className="input"
            placeholder="Add a checklist item"
            value={newSub}
            onChange={(e) => setNewSub(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addSub();
            }}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={!newSub.trim()}
            onClick={() => void addSub()}
          >
            Add
          </button>
        </div>
      </div>
    </Modal>
  );
}
